// LiabilityFormPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, getAuthHeaders } from "../utils/api";
import { showSuccessToast, showErrorToast } from "../components/Toast";
import Loader from "../components/Loader";
import AddLiability from "./AddLiability";

// Normalize API liability type to the values used by the Select/options in AddLiability
function normalizeApiLiability(raw) {
  if (!raw) return "mortgage";
  const s = String(raw).trim().toLowerCase();
  const map = {
    creditcard: "credit card",
    credit_card: "credit card",
    "credit card": "credit card",
    credit: "credit card",
    personaldebt: "personal debts",
    personaldebts: "personal debts",
    bnpl: "bnpl",
    buynowpaylater: "bnpl",
    insurancepremium: "insurance premium",
    taxpayable: "tax payable",
    overdraft: "overdraft",
    mortgage: "mortgage",
    vehicleloan: "vehicle loan",
    vehicle: "vehicle loan",
    loan: "loan",
    generalloan: "loan",
  };
  const cleaned = s.replace(/[^a-z0-9]/g, "");
  if (map[cleaned]) return map[cleaned];
  if (s.includes("credit")) return "credit card";
  if (s.includes("mortg")) return "mortgage";
  if (s.includes("vehicle") || s.includes("car")) return "vehicle loan";
  if (s.includes("bnpl") || s.includes("buy")) return "bnpl";
  if (s.includes("insurance") || s.includes("premium")) return "insurance premium";
  if (s.includes("tax")) return "tax payable";
  if (s.includes("overdraft")) return "overdraft";
  if (s.includes("personal") || s.includes("debt")) return "personal debts";
  if (s.includes("loan")) return "loan";
  return "mortgage";
}

// Map API object -> Formik initial values expected by AddLiability
function mapApiToFormValues(apiData = {}) {
  if (!apiData || Object.keys(apiData).length === 0) return null;

  // Normalize liability to match your Select option values
  const rawLiability = apiData.liabilityType || apiData.liability || apiData.type || apiData.category;
  const liability = normalizeApiLiability(rawLiability);

  const base = {
    liability,
    name: apiData.name || "",
    description: apiData.description || "",
    notes: apiData.notes || "",
    currency: apiData.currency || "USD",
    originalAmount: apiData.originalAmount ?? apiData.amount ?? 0,
    outstandingAmount: apiData.outstandingAmount ?? apiData.currentBalance ?? 0,
    interestRate: apiData.interestRate ?? 0,
    startDate: apiData.startDate ? apiData.startDate.slice(0, 10) : "",
    maturityDate: apiData.endDate ? apiData.endDate.slice(0, 10) : apiData.maturityDate || "",
    nextPaymentDate: apiData.nextPaymentDate ? apiData.nextPaymentDate.slice(0, 10) : "",
    tags: apiData.tags || [],
    files: apiData.files || apiData.images || apiData.photos || apiData.documents || [],
    documents: apiData.documents || [],
  };

  // credit card
  const cc = apiData.creditCardDetails || apiData.creditCard || apiData.cardDetails || {};
  if (liability === "credit card") {
    const flatCardName = cc.cardName || apiData.cardName || base.name;
    return {
      ...base,
      cardName: flatCardName,
      issuer: cc.issuer || "",
      creditLimit: cc.creditLimit ?? apiData.creditLimit ?? "",
      currentBalance: cc.currentBalance ?? apiData.currentBalance ?? base.outstandingAmount,
      annualPercentageRate: cc.annualPercentageRate ?? apiData.annualPercentageRate ?? "",
      minimumPayment: cc.minimumPayment ?? "",
      repaymentFrequency: cc.repaymentFrequency ?? "monthly",
      statementDate: cc.statementDate ? cc.statementDate.slice(0, 10) : "",
      paymentDueDate: cc.paymentDueDate ? cc.paymentDueDate.slice(0, 10) : "",
      creditCardDetails: {
        cardName: flatCardName,
        issuer: cc.issuer || "",
        creditLimit: cc.creditLimit ?? apiData.creditLimit ?? "",
        currentBalance: cc.currentBalance ?? apiData.currentBalance ?? base.outstandingAmount,
        annualPercentageRate: cc.annualPercentageRate ?? apiData.annualPercentageRate ?? "",
        minimumPayment: cc.minimumPayment ?? "",
        repaymentFrequency: cc.repaymentFrequency ?? "monthly",
        statementDate: cc.statementDate ? cc.statementDate.slice(0, 10) : "",
        paymentDueDate: cc.paymentDueDate ? cc.paymentDueDate.slice(0, 10) : "",
      },
    };
  }

  // mortgage
  const m = apiData.mortgageDetails || apiData.mortgage || {};
  if (liability === "mortgage") {
    return {
      ...base,
      propertyAddress: m.propertyAddress || "",
      lenderName: m.lenderName || apiData.lenderName || "",
      originalLoanAmount: m.originalLoanAmount ?? base.originalAmount,
      originalLoanCurrency: m.originalLoanCurrency ?? base.currency,
      loanStartDate: m.loanStartDate ? m.loanStartDate.slice(0, 10) : base.startDate,
      loanTerm: m.loanTermYears ?? m.loanTerm ?? "",
      monthlyRepayment: m.monthlyRepayment ?? "",
      repaymentType: m.repaymentType || "principalAndInterest",
      offsetAccountLinked: m.offsetAccountLinked ? "Yes" : "No",
      maturityDate: m.maturityDate ? m.maturityDate.slice(0, 10) : base.maturityDate,
      loanType: m.loanType, 
      outstandingAmount: apiData.outstandingAmount,
      mortgageDetails: { ...m },
    };
  }

  // vehicle loan
  const v = apiData.vehicleLoanDetails || apiData.vehicleLoan || apiData.vehicle || {};
  if (liability === "vehicle loan") {
    return {
      ...base,
      vehicleType: v.vehicleType || "car",
      vehicleName: v.vehicleName || v.name || "",
      makeModel: v.makeModel || "",
      registrationNumber: v.registrationNumber || "",
      ownershipType: v.ownershipType || "",
      lenderName: v.lenderName || apiData.lenderName || "",
      originalLoanAmount: v.originalLoanAmount ?? base.originalAmount,
      loanStartDate: v.loanStartDate ? v.loanStartDate.slice(0, 10) : base.startDate,
      loanTerm: v.loanTermYears ?? v.loanTermMonths ?? "",
      repaymentFrequency: v.repaymentFrequency || "",
      paymentDueDate: v.paymentDueDate ? v.paymentDueDate.slice(0, 10) : "",
      maturityDate: v.maturityDate ? v.maturityDate.slice(0, 10) : "",
      outstandingAmount: apiData.outstandingAmount,
      currency: v.currency || base.currency,
      vehicleLoanDetails: { ...v },
    };
  }

  // personal debts
  const p = apiData.personalDebtDetails || apiData.personalDebt || {};
  if (liability === "personal debts") {
    return {
      ...base,
      liabilityName: apiData.name || p.name || "",
      category: p.category || apiData.category || "",
      notes: apiData.notes || p.notes || base.notes,
      createdDate: apiData.startDate || apiData.createdAt || "",
      lastUpdated: apiData.updatedAt || "",
      status: p.status || apiData.status || "active",
      personalDebtDetails: { ...p },
    };
  }

  // bnpl
  const b = apiData.bnplDetails || apiData.buyNowPayLater || {};
  if (liability === "bnpl") {
    return {
      ...base,
      merchantName: b.merchantName || "",
      serviceProvider: b.serviceProvider || "",
      purchaseDescription: b.purchaseDescription || "",
      totalAmount: b.totalAmount ?? apiData.totalAmount ?? base.originalAmount,
      remainingBalance: b.remainingBalance ?? apiData.remainingBalance ?? base.outstandingAmount,
      nextPaymentDate: b.nextPaymentDate ? b.nextPaymentDate.slice(0, 10) : "",
      nextPaymentAmount: b.nextPaymentAmount ?? 0,
      remainingPayments: b.remainingPayments ?? 0,
      interestFees: b.interestFees ?? 0,
      paymentSchedule: b.paymentSchedule || "",
      bnplDetails: { ...b },
    };
  }

  // insurance premium
  const ins = apiData.insurancePremiumDetails || apiData.insurance || {};
  if (liability === "insurance premium") {
    return {
      ...base,
      insuranceType: ins.insuranceType || apiData.insuranceType || "health",
      insuranceCompany: ins.insuranceCompany || apiData.insuranceCompany || "",
      policyNumber: ins.policyNumber || apiData.policyNumber || "",
      premiumAmount: ins.premiumAmount || apiData.premiumAmount || base.outstandingAmount,
      dueDate: ins.dueDate ? ins.dueDate.slice(0, 10) : "",
      policyStartDate: ins.policyStartDate ? ins.policyStartDate.slice(0, 10) : "",
      policyEndDate: ins.policyEndDate ? ins.policyEndDate.slice(0, 10) : "",
      deductibleAmount: ins.deductibleAmount ?? 0,
      insurancePremiumDetails: { ...ins },
      repaymentFrequency: ins.repaymentFrequency || apiData.repaymentFrequency || "monthly"
    };
  }

  // tax payable
  const tax = apiData.taxPayableDetails || apiData.tax || {};
  if (liability === "tax payable") {
    return {
      ...base,
      taxType: tax.taxType || apiData.taxType || "income",
      taxYearPeriod: tax.taxYearPeriod || apiData.taxYearPeriod || "",
      amountDue: tax.amountDue ?? apiData.amountDue ?? base.originalAmount,
      dueDate: tax.dueDate ? tax.dueDate.slice(0, 10) : "",
      penalty: tax.penalty ?? 0,
      interestCharges: tax.interestCharges ?? 0,
      paymentStatus: tax.paymentStatus || "pending",
      taxPayableDetails: { ...tax },
    };
  }

  // loan fallback
  if (liability === "loan") {
    const gl = apiData.generalLoanDetails || apiData.loanDetails || {};
    return {
      ...base,
      loanPurposeType: gl.loanPurposeType || gl.loanPurpose || apiData.loanPurposeType || apiData.loanPurpose || "",
      loanType: gl.loanType || apiData.loanType || "personal",
      loanPurpose: gl.loanPurpose || apiData.loanPurpose || "",
      lenderName: gl.lenderName || apiData.lenderName || "",
      loanStartDate: gl.loanStartDate ? gl.loanStartDate.slice(0, 10) : base.startDate,
      loanTerm: gl.loanTermMonths ?? gl.loanTerm ?? "",
      repaymentFrequency: gl.repaymentFrequency || "",
      maturityDate: gl.maturityDate ? gl.maturityDate.slice(0, 10) : base.maturityDate,
      generalLoanDetails: { ...gl },
    };
  }

  // overdraft
  if (liability === "overdraft") {
    const od = apiData.overdraftDetails || apiData.overdraft || {};
    return {
      ...base,
        overdraft: od.overdraft || "",
        bankName: od.bankName || "",
        accountNumber: od.accountNumber || "",
        overdraftStartDate: od.overdraftStartDate ? od.overdraftStartDate.slice(0, 10) : base.startDate,
        currentOverdraftAmount: od.currentOverdraftAmount ?? apiData.currentOverdraftAmount ?? base.outstandingAmount,
        overdraftLimit: od.overdraftLimit ?? apiData.overdraftLimit ?? base.originalAmount,
        interestRate: od.interestRate ?? apiData.interestRate ?? 0,
        repaymentFrequency: od.repaymentFrequency || apiData.repaymentFrequency || "",
        overdraftFee: od.overdraftFee ?? 0,
        overdraftDetails: { ...od },
      };
          }

  // fallback: return base
  return base;
}

const LiabilityFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const [readOnly, setReadOnly] = useState(() => {
    // Check location.state.edit first (navigated from MyLiability)
    if (location.state?.edit !== undefined) {
      return !location.state.edit; // edit: true -> readOnly: false
    }
    // Default: if there's an id, default to view mode (readOnly)
    return !!id;
  });

  // Store original API values for comparison in edit mode
  const [apiInitialValues, setApiInitialValues] = useState(null);

  // Helper function to compare values - handles type coercion and empty string vs undefined cases
  // This matches the hasChanged function from AssetFormWrapper.js
  const hasChanged = (newValue, oldValue) => {
    // Normalize empty strings, null, and undefined to null for comparison purposes
    const normalizedNew = (newValue === '' || newValue === null || newValue === undefined) ? null : newValue;
    const normalizedOld = (oldValue === '' || oldValue === null || oldValue === undefined) ? null : oldValue;
    
    // If both are null/empty after normalization, no change
    if (normalizedNew === null && normalizedOld === null) return false;
    
    // If only one is null/empty after normalization, there's a change
    if (normalizedNew === null || normalizedOld === null) return true;
    
    // Compare as strings to handle type coercion (e.g., "506" vs 506)
    return String(normalizedNew).trim() !== String(normalizedOld).trim();
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .get(`/liabilities/${id}`, { headers: getAuthHeaders() })
      .then((res) => {
        const mapped = mapApiToFormValues(res.data);

        // IMPORTANT: pass the mapped object into AddLiability so Formik receives the expected shape
        setInitialValues(mapped);
        // Store original API values for comparison in edit mode
        setApiInitialValues(res.data);
      })
      .catch((e) => {
        console.error("GET /liabilities/:id failed", e);
        setError(e?.message || "Failed to fetch liability");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Detect mode from query param and location state
  useEffect(() => {
    // First check location.state (navigated from MyLiability)
    if (location.state?.edit !== undefined) {
      if (location.state.edit === true) {
        setMode("edit");
        setReadOnly(false);
        return;
      } else {
        setMode("view");
        setReadOnly(true);
        return;
      }
    }
    
    // Then check URL params
    const params = new URLSearchParams(window.location.search);
    const m = params.get("mode");
    if (m === "view") {
      setMode("view");
      setReadOnly(true);
    } else if (m === "edit") {
      setMode("edit");
      setReadOnly(false);
    } else {
      // Default behavior: if there's an id, default to view mode (readOnly)
      // unless explicitly in edit mode
      if (id) {
        setMode("view");
        setReadOnly(true);
      } else {
        setMode(null);
      }
    }
  }, [id, location.state]);

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">{error}</div>;

  // handleUpdate: delegate update (PATCH) to backend
  // IMPORTANT: this function rethrows the error after marking Formik errors so AddLiability can decide whether to call onSuccess()
  const handleUpdate = async (payload, formikHelpers) => {
    try {
      const headers = getAuthHeaders();
      
      if (id) {
        // Edit mode - PATCH request
        // Build payload with ONLY changed fields (matching AssetFormWrapper pattern)
        console.log("✏️ EDIT MODE - Initial values:", JSON.stringify(initialValues, null, 2));
        console.log("✏️ EDIT MODE - Current values:", JSON.stringify(payload, null, 2));
        
        const changedPayload = {};

        // Track if there are any changes
        let hasAnyChange = false;

        // Check isDraft only - this is the only field we always want to track
        if (hasChanged(payload.isDraft, initialValues?.isDraft)) {
          changedPayload.isDraft = payload.isDraft;
          hasAnyChange = true;
        }

        // Check top-level fields for changes - using individual if statements like Asset
        if (hasChanged(payload.name, initialValues?.name)) {
          changedPayload.name = payload.name;
          hasAnyChange = true;
        }
        if (hasChanged(payload.description, initialValues?.description)) {
          changedPayload.description = payload.description;
          hasAnyChange = true;
        }
        if (hasChanged(payload.notes, initialValues?.notes)) {
          changedPayload.notes = payload.notes;
          hasAnyChange = true;
        }
        if (hasChanged(payload.currency, initialValues?.currency)) {
          changedPayload.currency = payload.currency;
          hasAnyChange = true;
        }
        if (hasChanged(payload.originalAmount, initialValues?.originalAmount)) {
          changedPayload.originalAmount = payload.originalAmount;
          hasAnyChange = true;
        }
        if (hasChanged(payload.outstandingAmount, initialValues?.outstandingAmount)) {
          changedPayload.outstandingAmount = payload.outstandingAmount;
          hasAnyChange = true;
        }
        if (hasChanged(payload.interestRate, initialValues?.interestRate)) {
          changedPayload.interestRate = payload.interestRate;
          hasAnyChange = true;
        }
        if (hasChanged(payload.startDate, initialValues?.startDate)) {
          changedPayload.startDate = payload.startDate;
          hasAnyChange = true;
        }
        if (hasChanged(payload.maturityDate, initialValues?.maturityDate)) {
          changedPayload.maturityDate = payload.maturityDate;
          hasAnyChange = true;
        }
        if (hasChanged(payload.nextPaymentDate, initialValues?.nextPaymentDate)) {
          changedPayload.nextPaymentDate = payload.nextPaymentDate;
          hasAnyChange = true;
        }
        if (hasChanged(payload.endDate, initialValues?.endDate)) {
          changedPayload.endDate = payload.endDate;
          hasAnyChange = true;
        }

        // Handle subCategory-specific details based on liability type
        // Note: AddLiability.js sends liabilityType in the payload (API format: camelCase)
        // Form uses spaces (e.g., "credit card") but API uses camelCase (e.g., "creditCard")
        let liabilityType = payload.liabilityType || payload.liability;
        
        // Normalize liability type for comparison (handle both "credit card" and "creditCard")
        const liabilityTypeMap = {
          'creditCard': 'credit card',
          'vehicleLoan': 'vehicle loan',
          'generalLoan': 'loan',
          'taxPayable': 'tax payable',
          'credit card': 'credit card',
          'vehicle loan': 'vehicle loan',
          'loan': 'loan',
          'tax payable': 'tax payable',
          'personal debts': 'personal debts',
          'personalDebt': 'personal debts',
          'insurance premium': 'insurance premium',
          'insurancePremium': 'insurance premium',
          'bnpl': 'bnpl',
          'buyNowPayLater': 'bnpl',
          'overdraft': 'overdraft',
          'mortgage': 'mortgage'
        };
        
        // Normalize to the form format (with spaces)
        liabilityType = liabilityTypeMap[liabilityType] || liabilityType;
        
        if (liabilityType === "credit card") {
          const creditCardDetails = {};
          let hasCreditCardChange = false;
          
          // Compare each field individually - matching Asset pattern
          if (hasChanged(payload.creditCardDetails?.cardName, initialValues?.cardName)) {
            creditCardDetails.cardName = payload.creditCardDetails?.cardName;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.issuer, initialValues?.issuer)) {
            creditCardDetails.issuer = payload.creditCardDetails?.issuer;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.creditLimit, initialValues?.creditLimit)) {
            creditCardDetails.creditLimit = payload.creditCardDetails?.creditLimit;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.currentBalance, initialValues?.currentBalance)) {
            creditCardDetails.currentBalance = payload.creditCardDetails?.currentBalance;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.annualPercentageRate, initialValues?.annualPercentageRate)) {
            creditCardDetails.annualPercentageRate = payload.creditCardDetails?.annualPercentageRate;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.minimumPayment, initialValues?.minimumPayment)) {
            creditCardDetails.minimumPayment = payload.creditCardDetails?.minimumPayment;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.repaymentFrequency, initialValues?.repaymentFrequency)) {
            creditCardDetails.repaymentFrequency = payload.creditCardDetails?.repaymentFrequency;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.statementDate, initialValues?.statementDate)) {
            creditCardDetails.statementDate = payload.creditCardDetails?.statementDate;
            hasCreditCardChange = true;
          }
          if (hasChanged(payload.creditCardDetails?.paymentDueDate, initialValues?.paymentDueDate)) {
            creditCardDetails.paymentDueDate = payload.creditCardDetails?.paymentDueDate;
            hasCreditCardChange = true;
          }
          
          if (hasCreditCardChange) {
            changedPayload.creditCardDetails = creditCardDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "mortgage") {
          const mortgageDetails = {};
          let hasMortgageChange = false;
          
          if (hasChanged(payload.mortgageDetails?.propertyAddress, initialValues?.propertyAddress)) {
            mortgageDetails.propertyAddress = payload.mortgageDetails?.propertyAddress;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.lenderName, initialValues?.lenderName)) {
            mortgageDetails.lenderName = payload.mortgageDetails?.lenderName;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.originalLoanAmount, initialValues?.originalLoanAmount)) {
            mortgageDetails.originalLoanAmount = payload.mortgageDetails?.originalLoanAmount;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.originalLoanCurrency, initialValues?.originalLoanCurrency)) {
            mortgageDetails.originalLoanCurrency = payload.mortgageDetails?.originalLoanCurrency;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.loanStartDate, initialValues?.loanStartDate)) {
            mortgageDetails.loanStartDate = payload.mortgageDetails?.loanStartDate;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.loanTermYears, initialValues?.loanTerm)) {
            mortgageDetails.loanTermYears = payload.mortgageDetails?.loanTermYears;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.monthlyRepayment, initialValues?.monthlyRepayment)) {
            mortgageDetails.monthlyRepayment = payload.mortgageDetails?.monthlyRepayment;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.repaymentType, initialValues?.repaymentType)) {
            mortgageDetails.repaymentType = payload.mortgageDetails?.repaymentType;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.offsetAccountLinked, initialValues?.offsetAccountLinked)) {
            mortgageDetails.offsetAccountLinked = payload.mortgageDetails?.offsetAccountLinked;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.maturityDate, initialValues?.maturityDate)) {
            mortgageDetails.maturityDate = payload.mortgageDetails?.maturityDate;
            hasMortgageChange = true;
          }
          if (hasChanged(payload.mortgageDetails?.loanType, initialValues?.loanType)) {
            mortgageDetails.loanType = payload.mortgageDetails?.loanType;
            hasMortgageChange = true;
          }
          
          if (hasMortgageChange) {
            changedPayload.mortgageDetails = mortgageDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "vehicle loan") {
          const vehicleLoanDetails = {};
          let hasVehicleLoanChange = false;
          
          if (hasChanged(payload.vehicleLoanDetails?.vehicleType, initialValues?.vehicleType)) {
            vehicleLoanDetails.vehicleType = payload.vehicleLoanDetails?.vehicleType;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.vehicleName, initialValues?.vehicleName)) {
            vehicleLoanDetails.vehicleName = payload.vehicleLoanDetails?.vehicleName;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.makeModel, initialValues?.makeModel)) {
            vehicleLoanDetails.makeModel = payload.vehicleLoanDetails?.makeModel;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.registrationNumber, initialValues?.registrationNumber)) {
            vehicleLoanDetails.registrationNumber = payload.vehicleLoanDetails?.registrationNumber;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.ownershipType, initialValues?.ownershipType)) {
            vehicleLoanDetails.ownershipType = payload.vehicleLoanDetails?.ownershipType;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.lenderName, initialValues?.lenderName)) {
            vehicleLoanDetails.lenderName = payload.vehicleLoanDetails?.lenderName;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.originalLoanAmount, initialValues?.originalLoanAmount)) {
            vehicleLoanDetails.originalLoanAmount = payload.vehicleLoanDetails?.originalLoanAmount;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.loanStartDate, initialValues?.loanStartDate)) {
            vehicleLoanDetails.loanStartDate = payload.vehicleLoanDetails?.loanStartDate;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.loanTermMonths, initialValues?.loanTerm)) {
            vehicleLoanDetails.loanTermMonths = payload.vehicleLoanDetails?.loanTermMonths;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.repaymentFrequency, initialValues?.repaymentFrequency)) {
            vehicleLoanDetails.repaymentFrequency = payload.vehicleLoanDetails?.repaymentFrequency;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.paymentDueDate, initialValues?.paymentDueDate)) {
            vehicleLoanDetails.paymentDueDate = payload.vehicleLoanDetails?.paymentDueDate;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.maturityDate, initialValues?.maturityDate)) {
            vehicleLoanDetails.maturityDate = payload.vehicleLoanDetails?.maturityDate;
            hasVehicleLoanChange = true;
          }
          if (hasChanged(payload.vehicleLoanDetails?.currency, initialValues?.currency)) {
            vehicleLoanDetails.currency = payload.vehicleLoanDetails?.currency;
            hasVehicleLoanChange = true;
          }
          
          if (hasVehicleLoanChange) {
            changedPayload.vehicleLoanDetails = vehicleLoanDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "loan") {
          const generalLoanDetails = {};
          let hasLoanChange = false;
          
          if (hasChanged(payload.generalLoanDetails?.loanType, initialValues?.loanType)) {
            generalLoanDetails.loanType = payload.generalLoanDetails?.loanType;
            hasLoanChange = true;
          }
          if (hasChanged(payload.generalLoanDetails?.loanPurpose, initialValues?.loanPurpose)) {
            generalLoanDetails.loanPurpose = payload.generalLoanDetails?.loanPurpose;
            hasLoanChange = true;
          }
          if (hasChanged(payload.generalLoanDetails?.lenderName, initialValues?.lenderName)) {
            generalLoanDetails.lenderName = payload.generalLoanDetails?.lenderName;
            hasLoanChange = true;
          }
          if (hasChanged(payload.generalLoanDetails?.loanStartDate, initialValues?.loanStartDate)) {
            generalLoanDetails.loanStartDate = payload.generalLoanDetails?.loanStartDate;
            hasLoanChange = true;
          }
          if (hasChanged(payload.generalLoanDetails?.loanTermMonths, initialValues?.loanTerm)) {
            generalLoanDetails.loanTermMonths = payload.generalLoanDetails?.loanTermMonths;
            hasLoanChange = true;
          }
          if (hasChanged(payload.generalLoanDetails?.repaymentFrequency, initialValues?.repaymentFrequency)) {
            generalLoanDetails.repaymentFrequency = payload.generalLoanDetails?.repaymentFrequency;
            hasLoanChange = true;
          }
          if (hasChanged(payload.generalLoanDetails?.maturityDate, initialValues?.maturityDate)) {
            generalLoanDetails.maturityDate = payload.generalLoanDetails?.maturityDate;
            hasLoanChange = true;
          }
          if (hasChanged(payload.generalLoanDetails?.originalAmount, initialValues?.originalAmount)) {
            generalLoanDetails.originalAmount = payload.generalLoanDetails?.originalAmount;
            hasLoanChange = true;
          }
          
          if (hasLoanChange) {
            changedPayload.generalLoanDetails = generalLoanDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "personal debts") {
          const personalDebtDetails = {};
          let hasPersonalDebtChange = false;
          
          if (hasChanged(payload.personalDebtDetails?.category, initialValues?.category)) {
            personalDebtDetails.category = payload.personalDebtDetails?.category;
            hasPersonalDebtChange = true;
          }
          if (hasChanged(payload.personalDebtDetails?.notes, initialValues?.notes)) {
            personalDebtDetails.notes = payload.personalDebtDetails?.notes;
            hasPersonalDebtChange = true;
          }
          
          if (hasPersonalDebtChange) {
            changedPayload.personalDebtDetails = personalDebtDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "overdraft") {
          const overdraftDetails = {};
          let hasOverdraftChange = false;
          
          if (hasChanged(payload.overdraftDetails?.overdraft, initialValues?.overdraft)) {
            overdraftDetails.overdraft = payload.overdraftDetails?.overdraft;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.bankName, initialValues?.bankName)) {
            overdraftDetails.bankName = payload.overdraftDetails?.bankName;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.accountNumber, initialValues?.accountNumber)) {
            overdraftDetails.accountNumber = payload.overdraftDetails?.accountNumber;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.overdraftStartDate, initialValues?.overdraftStartDate)) {
            overdraftDetails.overdraftStartDate = payload.overdraftDetails?.overdraftStartDate;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.currentOverdraftAmount, initialValues?.currentOverdraftAmount)) {
            overdraftDetails.currentOverdraftAmount = payload.overdraftDetails?.currentOverdraftAmount;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.overdraftLimit, initialValues?.overdraftLimit)) {
            overdraftDetails.overdraftLimit = payload.overdraftDetails?.overdraftLimit;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.interestRate, initialValues?.interestRate)) {
            overdraftDetails.interestRate = payload.overdraftDetails?.interestRate;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.repaymentFrequency, initialValues?.repaymentFrequency)) {
            overdraftDetails.repaymentFrequency = payload.overdraftDetails?.repaymentFrequency;
            hasOverdraftChange = true;
          }
          if (hasChanged(payload.overdraftDetails?.overdraftFee, initialValues?.overdraftFee)) {
            overdraftDetails.overdraftFee = payload.overdraftDetails?.overdraftFee;
            hasOverdraftChange = true;
          }
          
          if (hasOverdraftChange) {
            changedPayload.overdraftDetails = overdraftDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "bnpl") {
          const bnplDetails = {};
          let hasBnplChange = false;
          
          if (hasChanged(payload.bnplDetails?.merchantName, initialValues?.merchantName)) {
            bnplDetails.merchantName = payload.bnplDetails?.merchantName;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.serviceProvider, initialValues?.serviceProvider)) {
            bnplDetails.serviceProvider = payload.bnplDetails?.serviceProvider;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.purchaseDescription, initialValues?.purchaseDescription)) {
            bnplDetails.purchaseDescription = payload.bnplDetails?.purchaseDescription;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.totalAmount, initialValues?.totalAmount)) {
            bnplDetails.totalAmount = payload.bnplDetails?.totalAmount;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.remainingBalance, initialValues?.remainingBalance)) {
            bnplDetails.remainingBalance = payload.bnplDetails?.remainingBalance;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.startDate, initialValues?.startDate)) {
            bnplDetails.startDate = payload.bnplDetails?.startDate;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.nextPaymentDate, initialValues?.nextPaymentDate)) {
            bnplDetails.nextPaymentDate = payload.bnplDetails?.nextPaymentDate;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.nextPaymentAmount, initialValues?.nextPaymentAmount)) {
            bnplDetails.nextPaymentAmount = payload.bnplDetails?.nextPaymentAmount;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.remainingPayments, initialValues?.remainingPayments)) {
            bnplDetails.remainingPayments = payload.bnplDetails?.remainingPayments;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.interestFees, initialValues?.interestFees)) {
            bnplDetails.interestFees = payload.bnplDetails?.interestFees;
            hasBnplChange = true;
          }
          if (hasChanged(payload.bnplDetails?.paymentSchedule, initialValues?.paymentSchedule)) {
            bnplDetails.paymentSchedule = payload.bnplDetails?.paymentSchedule;
            hasBnplChange = true;
          }
          
          if (hasBnplChange) {
            changedPayload.bnplDetails = bnplDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "insurance premium") {
          const insurancePremiumDetails = {};
          let hasInsurancePremiumChange = false;
          
          if (hasChanged(payload.insurancePremiumDetails?.premiumAmount, initialValues?.premiumAmount)) {
            insurancePremiumDetails.premiumAmount = payload.insurancePremiumDetails?.premiumAmount;
            hasInsurancePremiumChange = true;
          }
          if (hasChanged(payload.insurancePremiumDetails?.premiumFrequency, initialValues?.premiumFrequency)) {
            insurancePremiumDetails.premiumFrequency = payload.insurancePremiumDetails?.premiumFrequency;
            hasInsurancePremiumChange = true;
          }
          if (hasChanged(payload.insurancePremiumDetails?.dueDate, initialValues?.dueDate)) {
            insurancePremiumDetails.dueDate = payload.insurancePremiumDetails?.dueDate;
            hasInsurancePremiumChange = true;
          }
          if (hasChanged(payload.insurancePremiumDetails?.paymentStatus, initialValues?.paymentStatus)) {
            insurancePremiumDetails.paymentStatus = payload.insurancePremiumDetails?.paymentStatus;
            hasInsurancePremiumChange = true;
          }
          
          if (hasInsurancePremiumChange) {
            changedPayload.insurancePremiumDetails = insurancePremiumDetails;
            hasAnyChange = true;
          }
        }

        if (liabilityType === "tax payable") {
          const taxPayableDetails = {};
          let hasTaxPayableChange = false;
          
          if (hasChanged(payload.taxPayableDetails?.taxType, initialValues?.taxType)) {
            taxPayableDetails.taxType = payload.taxPayableDetails?.taxType;
            hasTaxPayableChange = true;
          }
          if (hasChanged(payload.taxPayableDetails?.taxYearPeriod, initialValues?.taxYearPeriod)) {
            taxPayableDetails.taxYearPeriod = payload.taxPayableDetails?.taxYearPeriod;
            hasTaxPayableChange = true;
          }
          if (hasChanged(payload.taxPayableDetails?.amountDue, initialValues?.amountDue)) {
            taxPayableDetails.amountDue = payload.taxPayableDetails?.amountDue;
            hasTaxPayableChange = true;
          }
          if (hasChanged(payload.taxPayableDetails?.dueDate, initialValues?.dueDate)) {
            taxPayableDetails.dueDate = payload.taxPayableDetails?.dueDate;
            hasTaxPayableChange = true;
          }
          if (hasChanged(payload.taxPayableDetails?.penalty, initialValues?.penalty)) {
            taxPayableDetails.penalty = payload.taxPayableDetails?.penalty;
            hasTaxPayableChange = true;
          }
          if (hasChanged(payload.taxPayableDetails?.interestCharges, initialValues?.interestCharges)) {
            taxPayableDetails.interestCharges = payload.taxPayableDetails?.interestCharges;
            hasTaxPayableChange = true;
          }
          if (hasChanged(payload.taxPayableDetails?.paymentStatus, initialValues?.paymentStatus)) {
            taxPayableDetails.paymentStatus = payload.taxPayableDetails?.paymentStatus;
            hasTaxPayableChange = true;
          }
          
          if (hasTaxPayableChange) {
            changedPayload.taxPayableDetails = taxPayableDetails;
            hasAnyChange = true;
          }
        }

        // Check for changes in photos, documents, and tags - only include if changed
        if (payload.files && JSON.stringify(payload.files) !== JSON.stringify(initialValues?.files)) {
          changedPayload.files = payload.files;
          hasAnyChange = true;
        }
        if (payload.photos && JSON.stringify(payload.photos) !== JSON.stringify(initialValues?.photos)) {
          changedPayload.photos = payload.photos;
          hasAnyChange = true;
        }
        if (payload.documents && JSON.stringify(payload.documents) !== JSON.stringify(initialValues?.documents)) {
          changedPayload.documents = payload.documents;
          hasAnyChange = true;
        }
        if (payload.tags && JSON.stringify(payload.tags) !== JSON.stringify(initialValues?.tags)) {
          changedPayload.tags = payload.tags;
          hasAnyChange = true;
        }

        // Check for changes in photos, documents, and tags - only include if changed
        // Use JSON.stringify comparison to detect actual changes
        if (payload.files && JSON.stringify(payload.files) !== JSON.stringify(initialValues?.files)) {
          changedPayload.files = payload.files;
          hasAnyChange = true;
        }
        if (payload.photos && JSON.stringify(payload.photos) !== JSON.stringify(initialValues?.photos)) {
          changedPayload.photos = payload.photos;
          hasAnyChange = true;
        }
        if (payload.documents && JSON.stringify(payload.documents) !== JSON.stringify(initialValues?.documents)) {
          changedPayload.documents = payload.documents;
          hasAnyChange = true;
        }
        if (payload.tags && JSON.stringify(payload.tags) !== JSON.stringify(initialValues?.tags)) {
          changedPayload.tags = payload.tags;
          hasAnyChange = true;
        }

        // Check if there are any changes - if not, skip API call
        if (!hasAnyChange) {
          showSuccessToast("No changes detected - liability is already up to date!");
          if (formikHelpers) formikHelpers.setSubmitting(false);
          navigate("/my-liability");
          return;
        }

        console.log("✏️ EDIT MODE - Final changedPayload:", JSON.stringify(changedPayload, null, 2));

        await api.patch(`/liabilities/${id}`, changedPayload, { headers });
        showSuccessToast(payload.isDraft ? "Draft saved." : "Liability updated successfully!");
      } else {
        // Create mode - send full payload
        const createRes = await api.post("/liabilities", payload, { headers });
        // Say "Draft saved" for drafts so users know it is not a finished liability (ML-13).
        showSuccessToast(payload.isDraft ? "Draft saved. You can finish it from the Liabilities list." : "Liability created successfully!");
        // Return the new liability ID so AddLiability can create reminders
        return createRes?.data?._id || createRes?.data?.id || null;
      }

      if (formikHelpers) formikHelpers.setSubmitting(false);
      // only navigate after success
      navigate("/my-liability");
      return null;
    } catch (e) {
      console.error("handleUpdate error:", e);
      // notify Formik of error if provided
      if (formikHelpers) {
        try {
          formikHelpers.setSubmitting(false);
          formikHelpers.setErrors({ submit: e?.message || "Failed to update liability" });
        } catch (inner) {
          // ignore if Formik helpers not compatible
        }
      }
      showErrorToast(e?.message || "Failed to update liability");
      // rethrow so AddLiability won't call onSuccess() automatically
      throw e;
    }
  };

  // Callback passed to AddLiability so child can request leaving readOnly mode.
  // AddLiability can call this when user clicks an Edit button inside the form.
  const enterEditMode = () => setReadOnly(false);

  return (
    <AddLiability
      initialValues={initialValues}
      liabilityId={id}
      readOnly={readOnly}
      onSubmit={handleUpdate}
      onSuccess={() => navigate("/my-liability")}
      // Give AddLiability a way to request parent to enter edit mode
      enterEditMode={() => setReadOnly(false)}
    />
  );
};

export default LiabilityFormPage;
